import { SceneFilterToggle } from "@/components/common/scene-filter-toggle";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import { TypeToggleButtonsLayout } from "@/modes/walk-mode/walk-overlay-responsive-layout";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName, deactivateTool } from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import { useCallback, type JSX } from "react";

type OverviewModeSceneFilterToggleProps = {
  /** The layout configuration for the button group */
  layout: TypeToggleButtonsLayout;
};

/**
 * @returns component to render a toggle button to switch the selected model type in overview mode
 */
export function OverviewModeSceneFilterToggle({
  layout,
}: OverviewModeSceneFilterToggleProps): JSX.Element {
  const dispatch = useAppDispatch();

  const currentArea = useCurrentArea();
  const currentScene = useCurrentScene();
  const hasPointClouds =
    !!currentArea.dataSessions3d.pointClouds.length ||
    !!currentArea.dataSessions5d.length;

  const hasGaussianSplats = !!currentArea.dataSessions3d.gaussianSplats.length;

  const mainElement = currentScene.main;
  const activeTool = useAppSelector(selectActiveTool);

  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);

  const onTypeChanged = useCallback(
    (value: SceneFilter) => {
      dispatch(setFlyWalkSceneFilter(value));
      // When the scene filter changes, the measurement tool is disabled.
      // Therefore we avoid the bug where the user starts a measurement
      // on the CAD, clicks "point cloud", and she cannot complete the
      // measurement because cross-model measures are forbidden.
      if (activeTool === ToolName.measurement) {
        dispatch(deactivateTool());
      }
    },
    [dispatch, activeTool],
  );

  return (
    <SceneFilterToggle
      value={sceneFilter}
      onValueChanged={onTypeChanged}
      onActiveElementChanged={(id) => dispatch(setActiveElement(id))}
      currentMainElement={mainElement}
      overlayEnabled={hasPointClouds}
      cadEnabled
      pointCloudEnabled={hasPointClouds}
      panoVisible={false}
      layout={layout}
      gaussianSplatsEnabled={hasGaussianSplats}
    />
  );
}
